import React, { Component } from 'react';
import { connect } from 'react-redux';
import { initialize } from 'redux-form';
import { normalize, schema } from 'normalizr';
import { loadPaymentHistories, exportPaymentHistories } from './actions';
import { PaymentHistoryMode } from './utils';
import Filter from './Filter';
import List from './List';
import authUtils from '../../auth/utils';
import { withTranslation } from 'react-i18next';
import SendMessageButton from '../../common/components/message/SendMessageButton';
import {
	sendMessage,
	PARENT_SEND_MESSAGE_REQUEST, PARENT_SEND_MESSAGE_SUCCESS, PARENT_SEND_MESSAGE_FAILURE
} from '../parents/actions';

export {
	PaymentHistoryMode
};

class PaymentHistories extends Component {
	constructor(props) {
		super(props);
		this.handleFilterSubmit = this.handleFilterSubmit.bind(this);
		this.handleFilterReset = this.handleFilterReset.bind(this);
		this.handleListEvent = this.handleListEvent.bind(this);
		this.handleListSelectionChange = this.handleListSelectionChange.bind(this);
		this.handleSendMessage = this.handleSendMessage.bind(this);
		this.state = {
			selectedEntities: []
		}
	}

	componentDidMount() {
		const { filter, sortKey, sortDirection, pageNumber, pageSize } = this.props.pagination;
		this.loadData({ filter, sortKey, sortDirection, pageNumber, pageSize });
		this.props.initializeFilter(filter);
	}

	loadData({ filter, sortKey, sortDirection, pageNumber, pageSize }) {
		const { mode } = this.props;
		filter = {
			...filter,
			isInstalmentPayment: mode === PaymentHistoryMode.INSTALMENTS ? true : null,
			hasOutstandingAmount: mode === PaymentHistoryMode.INSTALMENTS ? filter && filter.hasOutstandingAmount : null,
			isPaymentError: mode === PaymentHistoryMode.INSTALMENTS ? filter && filter.isPaymentError : null,
			isInactive: mode === PaymentHistoryMode.INSTALMENTS ? filter && filter.isInactive : null,
			hasCancellationRequested: filter && filter.hasCancellationRequested
		};
		this.props.loadEntities({ ...filter, sortKey, sortDirection, pageSize, pageNumber });
	}

	handleFilterSubmit(filter, exportFormat) {
		const { sortKey, sortDirection, pageSize } = this.props.pagination;
		this.setState({ selectedEntities: [] });
		this.loadData({ filter, sortKey, sortDirection, pageNumber: 1, pageSize });
		if (exportFormat) {
			this.exportData(filter, exportFormat);
		}
	}

	exportData(filter, exportFormat) {
		const { mode } = this.props;
		filter = {
			...filter,
			isInstalmentPayment: mode === PaymentHistoryMode.INSTALMENTS ? true : null,
			hasOutstandingAmount: mode === PaymentHistoryMode.INSTALMENTS ? filter && filter.hasOutstandingAmount : null,
			isPaymentError: mode === PaymentHistoryMode.INSTALMENTS ? filter && filter.isPaymentError : null,
			isInactive: mode === PaymentHistoryMode.INSTALMENTS ? filter && filter.isInactive : null,
			hasCancellationRequested: filter && filter.hasCancellationRequested
		};
		this.props.exportEntities(exportFormat, filter);
	}

	handleFilterReset() {
		this.handleFilterSubmit();
	}

	handleListEvent(listPagination) {
		const { filter } = this.props.pagination;
		this.loadData({ filter, ...listPagination });
	}

	handleListSelectionChange(newSelectedEntities) {
		this.setState({ selectedEntities: newSelectedEntities });
	}

	handleSendMessage(values, onSuccess) {
		const { sendMessage } = this.props;
		sendMessage({ ...values, parentIds: this.selectedParentIds() }, onSuccess);
	}

	selectedParentIds() {
		return [...new Set(this.state.selectedEntities.map(item => item.parent.id))]
	}

	normalizeData(payments) {
		const parent = new schema.Entity('parents', {}, {
			idAttribute: c => c.id
		});
		const payment = new schema.Entity('paymentHistories', { parent: parent }, {
			idAttribute: p => p.id
		});
		const normalizedData = normalize(payments, [payment]);
		return normalizedData;
	}

	renderListActions() {
		const { canSendMessage } = this.props;

		const normalizedData = this.normalizeData(this.state.selectedEntities);
		const parents = normalizedData && normalizedData.entities && normalizedData.entities.parents;

		return <React.Fragment>
			{canSendMessage && <SendMessageButton onSendMessage={this.handleSendMessage} to={parents && Object.values(parents).map(p => ({ name: p.fullname, email: p.email }))}
				block={PARENT_SEND_MESSAGE_REQUEST} unblock={[PARENT_SEND_MESSAGE_SUCCESS, PARENT_SEND_MESSAGE_FAILURE]} />}
		</React.Fragment>
	}

	render() {
		const { mode = PaymentHistoryMode.ALL, isReadonly, paymentTypes, paymentStatusTypes, paymentMethods, canSendMessage,
			showDiscountNameOnLessonPayments, enableManualInstalment, isSubscriptionComplianceEnabled } = this.props;
		return (
			<div>
				<Filter mode={mode} onSubmit={this.handleFilterSubmit} onReset={this.handleFilterReset} paymentTypes={paymentTypes}
					paymentStatusTypes={paymentStatusTypes} paymentMethods={paymentMethods} enableManualInstalment={enableManualInstalment}
					isSubscriptionComplianceEnabled={isSubscriptionComplianceEnabled} loading={this.props.pagination.loading || this.props.export.loading} />
				<List mode={mode} isReadonly={isReadonly} onFetchData={this.handleListEvent} data={this.props.data} {...this.props.pagination}
					selectedEntities={this.state.selectedEntities} onSelectionChange={this.handleListSelectionChange}
					listActions={this.renderListActions()} canSendMessage={canSendMessage}
					showDiscountNameOnLessonPayments={showDiscountNameOnLessonPayments}
				/>
			</div>
		);
	}
}

const mapStateToProps = (state, ownProps) => {
	const {
		pagination,
		exports,
		entities: { paymentHistories },
		lookups: { paymentTypes, paymentStatusTypes, paymentMethods },
		auth: { currentUser },
		configuration: { franchisor }
	} = state;

	const paymentHistoriesPagination = pagination.paymentHistories.all || { ids: [], pageNumber: 1, pageSize: 10 };
	const paymentHistoriesExport = exports.paymentHistories.all || {};

	return {
		data: paymentHistoriesPagination.ids.map(id => paymentHistories[id]),
		pagination: paymentHistoriesPagination,
		export: paymentHistoriesExport,
		isReadonly: authUtils.hasReadOnlyPermission(currentUser, 'PaymentHistory'),
		paymentTypes: paymentTypes,
		paymentStatusTypes: paymentStatusTypes,
		paymentMethods: paymentMethods,
		canSendMessage: franchisor.enableAdHocMessages && authUtils.hasPermission(currentUser, 'SendMessage'),
		showDiscountNameOnLessonPayments: franchisor.showDiscountNameOnLessonPayments,
		enableManualInstalment: franchisor.enableManualInstalment,
		isSubscriptionComplianceEnabled: franchisor.isSubscriptionComplianceEnabled
	};
};

export default connect(mapStateToProps, {
	loadEntities: loadPaymentHistories,
	exportEntities: exportPaymentHistories,
	sendMessage,
	initializeFilter: (data, fields) => initialize('paymentHistoriesFilter', data, fields)
})(withTranslation()(PaymentHistories));